// Optimistic patching of the cached GET responses when a mutation is queued offline.
// Each patcher receives { method, path, body, tempId, cache } and returns the synthetic response data.

const today = () => new Date().toISOString().slice(0, 10);
const num = (v) => (Number.isFinite(parseFloat(v)) ? parseFloat(v) : 0);

const DEFAULT_LOTS = [
  ['1', "PILOTAGE DES INTERVENANTS DIRECTS / BUREAU D'ETUDE"], ['2', 'GARDIENNAGE'], ['3', 'PALISSADE'],
  ['4', 'DEPOSE / DÉMOLITION'], ['5', 'MAÇONNERIE'], ['6', 'PROTECTION COUPE FEU'], ['7', 'VITRINE FACADE HABILLAGE'],
  ['8', 'SERRURERIE'], ['9', 'DOUBLAGE - CLOISONNEMENT - FAUX-PLAFOND'], ['10', 'PEINTURE'], ['11', 'REVETEMENT DE SOL'],
  ['12', 'ELECTRICITE'], ['13', 'CLIMATISATION - RAC - VMC'], ['14', 'PLOMBERIE SANITAIRES'],
  ['15', 'MENUISERIE - POSE MOBILIER'], ['16', 'SECURITE INCENDIE'], ['17', 'NETTOYAGE'], ['18', 'REMISE COMMERCIAL'],
].map(([id, nom]) => ({ id, nom }));

const recomputeDevis = (devis) => {
  devis.lots.forEach((lot) => {
    lot.postes.forEach((p) => { p.prix_total = num(p.quantite) * num(p.prix_unitaire); });
    lot.total_ht = lot.postes.reduce((s, p) => s + p.prix_total, 0);
  });
  devis.total_ht_maag = devis.lots.reduce((s, l) => s + l.total_ht, 0);
  devis.total_ht = devis.total_ht_maag + num(devis.total_ht_fournisseurs_directs);
  devis.tva = devis.total_ht_maag * 0.2;
  devis.total_ttc = devis.total_ht_maag + devis.tva;
  return devis;
};

const recomputeMargeLot = (lot) => {
  lot.cout = (lot.fournisseurs || []).reduce((s, f) => s + num(f.montant_ht_effectif ?? f.montant_ht), 0);
  lot.marge_euros = num(lot.vendu) - lot.cout;
  lot.marge_pourcentage = lot.vendu > 0 ? (lot.marge_euros / lot.vendu) * 100 : null;
};

const updateListItem = async (cache, key, id, updater) => {
  const list = await cache.get(key);
  if (!Array.isArray(list)) return;
  const idx = list.findIndex((x) => x.id === id);
  if (idx >= 0) { list[idx] = updater(list[idx]); await cache.set(key, list); }
};

const syncDevisInLists = async (cache, devis) => {
  const summary = (d) => ({ ...d, objet: devis.objet, statut: devis.statut, total_ht: devis.total_ht, total_ht_maag: devis.total_ht_maag, total_ttc: devis.total_ttc, version: devis.version });
  await updateListItem(cache, `/projets/${devis.projet_id}/devis-maag`, devis.id, summary);
  await updateListItem(cache, '/devis-maag', devis.id, summary);
};

const findDevisWithPoste = async (cache, posteId) => {
  for (const key of cache.keys()) {
    if (!/^\/devis-maag\/[^/?]+$/.test(key)) continue;
    const devis = await cache.get(key);
    for (const lot of devis?.lots || []) {
      const p = lot.postes.find((x) => x.id === posteId);
      if (p) return { key, devis, lot, poste: p };
    }
  }
  return null;
};

const saveDevis = async (cache, devis) => {
  recomputeDevis(devis);
  await cache.set(`/devis-maag/${devis.id}`, devis);
  await syncDevisInLists(cache, devis);
  return devis;
};

const patchers = [
  // ---- PROJETS ----
  {
    match: (m, p) => m === 'post' && p === '/projets',
    apply: async ({ body, tempId, cache }) => {
      const enseignes = (await cache.get('/enseignes')) || [];
      const ens = enseignes.find((e) => e.id === body.enseigne_id);
      const projet = {
        id: tempId, enseigne_nom: ens?.nom || '', ville: '', adresse: '', statut: 'en_preparation', statut_display: 'En préparation',
        date_debut: '', date_fin: '', responsable: '', commentaires: '', surface_commerciale: 0, surface_annexes: 0,
        client_paye: false, artisans_payes: false, is_cloture: false, devis_reference_ids: [], devis_used_ids: [],
        client_facturation: '', adresse_facturation: '', contact_facturation: '', telephone_facturation: '', email_facturation: '',
        server_path: '', ca_maag: 0, total_achats_artisans: 0, marge_euros: 0, marge_pourcentage: null, created_at: new Date().toISOString(),
        ...body, _offline_pending: true,
      };
      projet.nom_complet = `${projet.enseigne_nom} - ${projet.ville}`;
      await cache.set(`/projets/${tempId}`, projet);
      const list = await cache.get('/projets');
      if (Array.isArray(list)) await cache.set('/projets', [projet, ...list]);
      const ensList = await cache.get(`/enseignes/${body.enseigne_id}/projets`);
      if (Array.isArray(ensList)) await cache.set(`/enseignes/${body.enseigne_id}/projets`, [projet, ...ensList]);
      await cache.set(`/projets/${tempId}/devis-maag`, []);
      await cache.set(`/projets/${tempId}/fournisseurs-directs`, []);
      await cache.set(`/projets/${tempId}/achats-artisans`, []);
      await cache.set(`/projets/${tempId}/factures-artisans`, []);
      await cache.set(`/projets/${tempId}/plannings`, []);
      await cache.set(`/projets/${tempId}/bdc`, []);
      await cache.set(`/projets/${tempId}/factures-client`, []);
      const lots = (await cache.get('/lots')) || DEFAULT_LOTS;
      await cache.set(`/projets/${tempId}/marge`, {
        projet_id: tempId, devis_reference_id: null, devis_options: [],
        marges_par_lot: lots.map((l) => ({ lot_id: l.id, lot_nom: l.nom, vendu: 0, cout: 0, marge_euros: 0, marge_pourcentage: null, fournisseurs: [] })),
        total_factures_recues: 0, total_factures_payees: 0, total_factures_count: 0, ca_maag: 0, total_achats_artisans: 0, marge_euros: 0, marge_pourcentage: null,
      });
      return projet;
    },
  },
  {
    match: (m, p) => m === 'put' && /^\/projets\/[^/]+$/.test(p),
    apply: async ({ path, body, cache }) => {
      const id = path.split('/')[2];
      const projet = { ...((await cache.get(path)) || { id }), ...body };
      projet.nom_complet = `${projet.enseigne_nom || ''} - ${projet.ville || ''}`;
      await cache.set(path, projet);
      await updateListItem(cache, '/projets', id, (p) => ({ ...p, ...body, nom_complet: projet.nom_complet }));
      return projet;
    },
  },
  // ---- DEVIS ----
  {
    match: (m, p) => m === 'post' && p === '/devis-maag',
    apply: async ({ body, tempId, cache }) => {
      const projet = await cache.get(`/projets/${body.projet_id}`);
      const lots = (await cache.get('/lots')) || DEFAULT_LOTS;
      const devis = {
        id: tempId, projet_id: body.projet_id, projet_nom: projet?.nom_complet || '', enseigne_nom: projet?.enseigne_nom || '',
        objet: body.objet || '', date_remise: today(), version: body.version || '1', statut: 'brouillon', type_code: body.type_code || null,
        titre_devis: 'devis_travaux', titre_devis_custom: '', contact_maag: '', interlocuteur_client: '',
        lots: lots.map((l) => ({ id: l.id, nom: l.nom, postes: [], total_ht: 0 })),
        total_ht: 0, total_ht_maag: 0, total_ht_fournisseurs_directs: 0, fournisseurs_directs: [], tva: 0, total_ttc: 0,
        signature_image: '', signataire_nom: '', date_signature: '', has_signature_link: false,
        validite_devis: '30 jours', conditions_paiement: '40% à la commande, 30% à mi-travaux, 30% à la réception', delai_travaux: '', langue_pdf: 'fr',
        surface_commerciale: projet?.surface_commerciale || 0, surface_annexes: projet?.surface_annexes || 0, created_at: new Date().toISOString(), _offline_pending: true,
      };
      await cache.set(`/devis-maag/${tempId}`, devis);
      for (const key of [`/projets/${body.projet_id}/devis-maag`, '/devis-maag']) {
        const list = await cache.get(key);
        if (Array.isArray(list)) await cache.set(key, [devis, ...list]);
      }
      return devis;
    },
  },
  {
    match: (m, p) => m === 'post' && p === '/devis-maag/duplicate',
    apply: async ({ body, tempId, cache }) => {
      const src = await cache.get(`/devis-maag/${body.source_devis_id}`);
      if (!src) return { id: tempId, ...body };
      const devis = JSON.parse(JSON.stringify(src));
      devis.id = tempId; devis.projet_id = body.projet_id || src.projet_id; devis.objet = body.objet || src.objet;
      devis.version = body.version || '1'; devis.statut = 'brouillon'; devis.signature_image = ''; devis.signataire_nom = ''; devis.date_signature = '';
      devis.has_signature_link = false; devis._offline_pending = true;
      devis.lots.forEach((l) => l.postes.forEach((p) => { p.id = `tmp-${crypto.randomUUID()}`; }));
      await cache.set(`/devis-maag/${tempId}`, devis);
      for (const key of [`/projets/${devis.projet_id}/devis-maag`, '/devis-maag']) {
        const list = await cache.get(key);
        if (Array.isArray(list)) await cache.set(key, [devis, ...list]);
      }
      return devis;
    },
  },
  {
    match: (m, p) => m === 'put' && /^\/devis-maag\/[^/?]+$/.test(p),
    apply: async ({ path, body, cache }) => {
      const devis = await cache.get(path);
      if (!devis) return body;
      Object.assign(devis, body);
      return saveDevis(cache, devis);
    },
  },
  {
    match: (m, p) => m === 'delete' && /^\/devis-maag\/[^/?]+$/.test(p),
    apply: async ({ path, cache }) => {
      const devis = await cache.get(path);
      await cache.del(path);
      if (devis) {
        for (const key of [`/projets/${devis.projet_id}/devis-maag`, '/devis-maag']) {
          const list = await cache.get(key);
          if (Array.isArray(list)) await cache.set(key, list.filter((d) => d.id !== devis.id));
        }
      }
      return { ok: true };
    },
  },
  // ---- POSTES ----
  {
    match: (m, p) => m === 'post' && /^\/devis-maag\/[^/]+\/postes(-enriched)?(\?.*)?$/.test(p),
    apply: async ({ path, body, tempId, cache }) => {
      const devisId = path.split('/')[2];
      const enriched = path.includes('postes-enriched');
      const lotId = enriched ? body.lot_id : new URLSearchParams(path.split('?')[1] || '').get('lot_id');
      const description = enriched ? [body.designation, body.description].filter(Boolean).join(' - ') : body.description;
      const poste = {
        id: tempId, lot_id: lotId, sous_lot_id: body.sous_lot_id || null, sous_lot_nom: body.sous_lot_nom || null,
        sous_sous_lot_nom: body.sous_sous_lot_nom ? String(body.sous_sous_lot_nom).toUpperCase() : null, groupe_nom: body.groupe_nom || null,
        is_titre_libre: !!body.is_titre_libre, description: description || '', unite: body.unite || 'U', quantite: num(body.quantite),
        prix_unitaire: num(body.prix_unitaire), localisation: body.localisation || null, commentaire: body.commentaire || null,
        display_order: body.display_order ?? null, _offline_pending: true,
      };
      poste.prix_total = poste.quantite * poste.prix_unitaire;
      const devis = await cache.get(`/devis-maag/${devisId}`);
      if (devis) {
        const lot = devis.lots.find((l) => l.id === lotId);
        if (lot) { lot.postes.push(poste); await saveDevis(cache, devis); }
      }
      return enriched ? { poste, enrichment: { base_maag_created: false, prix_enseigne_created: false, devis_type_created: false }, ...poste } : poste;
    },
  },
  {
    match: (m, p) => m === 'put' && /^\/postes\/[^/]+$/.test(p),
    apply: async ({ path, body, cache }) => {
      const found = await findDevisWithPoste(cache, path.split('/')[2]);
      if (!found) return body;
      Object.assign(found.poste, body);
      if (body.lot_id && body.lot_id !== found.lot.id) {
        found.lot.postes = found.lot.postes.filter((x) => x.id !== found.poste.id);
        const target = found.devis.lots.find((l) => l.id === body.lot_id);
        if (target) target.postes.push(found.poste);
      }
      await saveDevis(cache, found.devis);
      return found.poste;
    },
  },
  {
    match: (m, p) => m === 'delete' && /^\/postes\/[^/]+$/.test(p),
    apply: async ({ path, cache }) => {
      const found = await findDevisWithPoste(cache, path.split('/')[2]);
      if (found) {
        found.lot.postes = found.lot.postes.filter((x) => x.id !== found.poste.id);
        await saveDevis(cache, found.devis);
      }
      return { ok: true };
    },
  },
  {
    match: (m, p) => m === 'delete' && /^\/devis\/[^/]+\/lots\/[^/]+\/clear$/.test(p),
    apply: async ({ path, cache }) => {
      const [, , devisId, , lotId] = path.split('/');
      const devis = await cache.get(`/devis-maag/${devisId}`);
      if (devis) {
        const lot = devis.lots.find((l) => l.id === lotId);
        if (lot) { lot.postes = []; await saveDevis(cache, devis); }
      }
      return { ok: true };
    },
  },
  {
    match: (m, p) => m === 'delete' && /^\/devis\/[^/]+\/postes\/bulk$/.test(p),
    apply: async ({ path, body, cache }) => {
      const devis = await cache.get(`/devis-maag/${path.split('/')[2]}`);
      const ids = new Set(Array.isArray(body) ? body : []);
      if (devis) {
        devis.lots.forEach((l) => { l.postes = l.postes.filter((p) => !ids.has(p.id)); });
        await saveDevis(cache, devis);
      }
      return { deleted: ids.size };
    },
  },
  // ---- LOTS PERSONNALISÉS ----
  {
    match: (m, p) => ['post', 'patch', 'delete'].includes(m) && /^\/devis-maag\/[^/]+\/lots(\/[^/]+)?$/.test(p),
    apply: async ({ method, path, body, cache }) => {
      const parts = path.split('/');
      const devisId = parts[2];
      const lotId = parts[4];
      const devis = await cache.get(`/devis-maag/${devisId}`);
      if (!devis) return body;
      if (method === 'post' && lotId === 'reset') {
        const lots = (await cache.get('/lots')) || DEFAULT_LOTS;
        devis.lots = lots.map((l) => ({ id: l.id, nom: l.nom, postes: devis.lots.find((x) => x.id === l.id)?.postes || [], total_ht: 0 }));
      } else if (method === 'post') {
        const ids = devis.lots.map((l) => parseInt(l.id, 10)).filter((n) => !Number.isNaN(n));
        devis.lots.push({ id: String((ids.length ? Math.max(...ids) : 0) + 1), nom: String(body.nom || '').trim().toUpperCase(), postes: [], total_ht: 0 });
      } else if (method === 'patch') {
        const lot = devis.lots.find((l) => l.id === lotId);
        if (lot) lot.nom = String(body.nom || '').trim().toUpperCase();
      } else if (method === 'delete') {
        devis.lots = devis.lots.filter((l) => l.id !== lotId);
      }
      return saveDevis(cache, devis);
    },
  },
  // ---- MARGE : slots fournisseurs ----
  {
    match: (m, p) => m === 'post' && /^\/projets\/[^/]+\/fournisseurs-lots$/.test(p),
    apply: async ({ path, body, tempId, cache }) => {
      const projetId = path.split('/')[2];
      const f = {
        id: tempId, projet_id: projetId, lot_id: body.lot_id, slot: body.slot || 1, entreprise: body.entreprise || '', montant_ht: num(body.montant_ht),
        montant_ht_effectif: num(body.montant_ht), facturation_progress: 0, factures_count: 0, legacy: false, locked: false,
        bdc_id: null, bdc_numero: null, devis_fichier: null, ...body, _offline_pending: true,
      };
      const marge = await cache.get(`/projets/${projetId}/marge`);
      if (marge) {
        const lot = marge.marges_par_lot.find((l) => l.lot_id === body.lot_id);
        if (lot) { lot.fournisseurs = [...(lot.fournisseurs || []), f]; recomputeMargeLot(lot); await cache.set(`/projets/${projetId}/marge`, marge); }
      }
      return f;
    },
  },
  {
    match: (m, p) => ['put', 'patch', 'delete'].includes(m) && /^\/projets\/[^/]+\/fournisseurs-lots\/[^/]+$/.test(p),
    apply: async ({ method, path, body, cache }) => {
      const [, , projetId, , fid] = path.split('/');
      const marge = await cache.get(`/projets/${projetId}/marge`);
      let result = body || { ok: true };
      if (marge) {
        marge.marges_par_lot.forEach((lot) => {
          const idx = (lot.fournisseurs || []).findIndex((f) => f.id === fid);
          if (idx < 0) return;
          if (method === 'delete') lot.fournisseurs.splice(idx, 1);
          else {
            const f = { ...lot.fournisseurs[idx], ...body };
            if (body && body.montant_ht !== undefined && !f.factures_count) f.montant_ht_effectif = num(body.montant_ht);
            lot.fournisseurs[idx] = f; result = f;
          }
          recomputeMargeLot(lot);
        });
        await cache.set(`/projets/${projetId}/marge`, marge);
      }
      return result;
    },
  },
  // ---- ENSEIGNES ----
  {
    match: (m, p) => m === 'post' && p === '/enseignes',
    apply: async ({ body, tempId, cache }) => {
      const ens = { id: tempId, logo_url: null, contacts: [], created_at: new Date().toISOString(), ...body, _offline_pending: true };
      const list = await cache.get('/enseignes');
      if (Array.isArray(list)) await cache.set('/enseignes', [ens, ...list]);
      await cache.set(`/enseignes/${tempId}`, ens);
      await cache.set(`/enseignes/${tempId}/projets`, []);
      return ens;
    },
  },
];

export const isQueueable = (method, path) => patchers.some((p) => p.match(method, path));

export const applyOfflineMutation = async (ctx) => {
  const patcher = patchers.find((p) => p.match(ctx.method, ctx.path));
  if (!patcher) return { id: ctx.tempId, ...(ctx.body || {}) };
  try {
    return await patcher.apply(ctx);
  } catch (e) {
    console.warn('[offline] patch failed', ctx.path, e);
    return { id: ctx.tempId, ...(ctx.body || {}) };
  }
};
